import { z } from 'zod';
import type { RoleKeyName } from './auth';

/** Módulos do PRD §10 (Integrações fora do escopo — DEC-001), com o nome de exibição. */
export const MODULE_LABELS = {
  'central-empresas': 'Central de Empresas',
  'visao-geral': 'Visão Geral da Empresa',
  diagnostico: 'Diagnóstico Empresarial',
  priorizacao: 'Matriz de Priorização',
  'planejamento-estrategico': 'Planejamento Estratégico',
  'mapa-estrategico': 'Mapa Estratégico BSC',
  objetivos: 'Objetivos Estratégicos',
  indicadores: 'Indicadores / KPIs',
  'metas-resultados': 'Metas e Resultados',
  'previsto-realizado': 'Previsto x Realizado',
  cenarios: 'Planejamento de Cenários',
  iniciativas: 'Iniciativas Estratégicas',
  'planos-acao': 'Planos de Ação',
  'dashboard-executivo': 'Dashboard Executivo',
  'dashboard-perspectiva': 'Dashboard por Perspectiva',
  'dashboard-area': 'Dashboard por Área',
  'analise-critica': 'Análise Crítica',
  'reunioes-decisoes': 'Reuniões e Decisões',
  alertas: 'Alertas e Desvios',
  relatorios: 'Relatórios',
  importacoes: 'Importações',
  administracao: 'Administração e Permissões',
  auditoria: 'Auditoria e Histórico',
} as const;

export type ModuleKey = keyof typeof MODULE_LABELS;
export const MODULE_KEYS = Object.keys(MODULE_LABELS) as [ModuleKey, ...ModuleKey[]];

/** Ações do PRD §20. */
export const ACTION_LABELS = {
  view: 'Visualizar',
  create: 'Criar',
  edit: 'Editar',
  delete: 'Excluir/arquivar',
  approve: 'Aprovar',
  export: 'Exportar',
  admin: 'Administrar',
} as const;

export type ActionKey = keyof typeof ACTION_LABELS;
export const ACTION_KEYS = Object.keys(ACTION_LABELS) as [ActionKey, ...ActionKey[]];

/** Permissões de um perfil: para cada módulo, as ações permitidas (módulo ausente = sem acesso). */
export type PermissionMatrix = Partial<Record<ModuleKey, ActionKey[]>>;

/** Formato da matriz (respostas da API). */
export const permissionMatrixShape = z.partialRecord(
  z.enum(MODULE_KEYS),
  z.array(z.enum(ACTION_KEYS)).max(ACTION_KEYS.length),
);

/**
 * Ações que têm efeito em cada módulo (T-281; RN-24; DEC-088): as que alguma rota da API confere — "visualizar" dá o
 * acesso ao módulo; as demais, só onde há o que criar, editar, excluir, aprovar, exportar ou administrar. A matriz não
 * aceita outras: a API recusa, o banco recusa (role_permission_applicable_actions, a mesma lista) e a tela de perfis
 * nem mostra a caixa. A API não sobe com uma rota que confira ação fora desta lista.
 */
export const MODULE_ACTIONS: Record<ModuleKey, readonly ActionKey[]> = {
  'central-empresas': ['view', 'create', 'edit', 'delete', 'admin'],
  'visao-geral': ['view'],
  // "Excluir" modera comentários e arquivos dos itens de diagnóstico (DEC-047).
  diagnostico: ['view', 'create', 'edit', 'delete'],
  priorizacao: ['view', 'edit', 'delete'],
  'planejamento-estrategico': ['view', 'create', 'edit', 'delete'],
  'mapa-estrategico': ['view'],
  objetivos: ['view', 'create', 'edit', 'delete'],
  indicadores: ['view', 'create', 'edit', 'delete'],
  // Lançar realizados e metas é "editar"; "aprovar" metas (quando a aprovação está ligada); "administrar" fecha e
  // reabre períodos.
  'metas-resultados': ['view', 'edit', 'approve', 'admin'],
  // Orçamento (T-203; DEC-065).
  'previsto-realizado': ['view', 'create', 'edit', 'delete'],
  cenarios: ['view', 'create', 'edit', 'delete'],
  iniciativas: ['view', 'create', 'edit', 'delete'],
  'planos-acao': ['view', 'create', 'edit', 'delete'],
  'dashboard-executivo': ['view'],
  'dashboard-perspectiva': ['view'],
  'dashboard-area': ['view'],
  // Conduzir (pauta, revisão, justificativas e decisões) é "editar"; agendar a reunião é de Reuniões.
  'analise-critica': ['view', 'edit'],
  // "Aprovar" vota as decisões das análises críticas (RF-48).
  'reunioes-decisoes': ['view', 'create', 'edit', 'delete', 'approve'],
  alertas: ['view', 'create', 'edit'],
  // Baixar relatórios (PDF, Excel, CSV) é "exportar" (DEC-068).
  relatorios: ['view', 'export'],
  importacoes: ['view', 'create', 'delete'],
  administracao: ['view', 'create', 'edit', 'delete', 'admin'],
  auditoria: ['view'],
};

/** A ação tem efeito no módulo (está em MODULE_ACTIONS). */
export const actionApplies = (module: ModuleKey, action: ActionKey): boolean =>
  MODULE_ACTIONS[module].includes(action);

/**
 * Módulos que dependem de "visualizar" em outro (T-280; DEC-087): a análise crítica acontece nas reuniões — a lista,
 * o detalhe, a ata, os comentários e as menções são do módulo de Reuniões e Decisões.
 */
export const MODULE_VIEW_DEPENDENCIES: Partial<Record<ModuleKey, ModuleKey>> = {
  'analise-critica': 'reunioes-decisoes',
};

/** Matriz recebida (edição) ou lida do banco: só ações com efeito no módulo (MODULE_ACTIONS) e normalizada. */
export const permissionMatrixSchema = permissionMatrixShape
  .superRefine((matrix, ctx) => {
    for (const [module, actions] of Object.entries(matrix) as [ModuleKey, ActionKey[]][]) {
      for (const action of actions) {
        if (!actionApplies(module, action)) {
          ctx.addIssue({
            code: 'custom',
            path: [module],
            message: `"${ACTION_LABELS[action]}" não se aplica a ${MODULE_LABELS[module]}.`,
          });
        }
      }
    }
  })
  .transform((matrix) => {
    // Normaliza: sem repetição, na ordem canônica; "visualizar" é pré-requisito das demais ações e dos módulos que
    // dependem deste (MODULE_VIEW_DEPENDENCIES).
    const sets = new Map<ModuleKey, Set<ActionKey>>();
    for (const key of MODULE_KEYS) {
      const actions = matrix[key];
      if (actions?.length) sets.set(key, new Set<ActionKey>([...actions, 'view']));
    }
    for (const [module, required] of Object.entries(MODULE_VIEW_DEPENDENCIES) as [ModuleKey, ModuleKey][]) {
      if (!sets.has(module)) continue;
      const set = sets.get(required) ?? new Set<ActionKey>();
      set.add('view');
      sets.set(required, set);
    }
    const out: PermissionMatrix = {};
    for (const key of MODULE_KEYS) {
      const set = sets.get(key);
      if (set) out[key] = ACTION_KEYS.filter((a) => set.has(a));
    }
    return out;
  });

/** Escopo por área/unidade (RN-24): ALL = toda a empresa; OWN = só itens das suas áreas/unidades. */
export const AREA_SCOPES = ['ALL', 'OWN'] as const;
export type AreaScopeName = (typeof AREA_SCOPES)[number];
export const AREA_SCOPE_LABELS: Record<AreaScopeName, string> = {
  ALL: 'Toda a empresa',
  OWN: 'Só as próprias áreas e unidades',
};

const ALL_ACTIONS = [...ACTION_KEYS];
const all = (modules: readonly ModuleKey[], actions: ActionKey[]): PermissionMatrix =>
  Object.fromEntries(modules.map((m) => [m, actions]));

const CONSULTANT_MODULES = MODULE_KEYS.filter((m) => m !== 'administracao');

/**
 * Permissões iniciais de cada perfil (PRD §9) — a SEMENTE da matriz editável por empresa. Cada
 * empresa recebe uma cópia ao criar os perfis; daí em diante vale o que estiver no banco.
 */
const SEED_ROLE_PERMISSIONS: Record<RoleKeyName, PermissionMatrix> = {
  // §9.1: cria e administra empresas, configura projetos, cria usuários, acessa todos os módulos.
  FLUXUS_ADMIN: all(MODULE_KEYS, ALL_ACTIONS),
  // §9.2: conduz diagnóstico, priorização, planejamento, BSC, análises, planos e relatórios.
  FLUXUS_CONSULTANT: {
    ...all(CONSULTANT_MODULES, ['view', 'create', 'edit', 'delete', 'approve', 'export']),
    // §9.1/§11.2: criar e editar empresa cliente sim; arquivar/administrar empresas é do Admin.
    'central-empresas': ['view', 'create', 'edit', 'export'],
    auditoria: ['view', 'export'],
    // DEC-004 item 19: fechar e reabrir períodos (consultor/admin).
    'metas-resultados': ['view', 'create', 'edit', 'delete', 'approve', 'export', 'admin'],
  },
  // §9.3: visão executiva; aprova e decide.
  EXECUTIVE: {
    ...all(
      ['visao-geral', 'mapa-estrategico', 'indicadores', 'previsto-realizado', 'alertas', 'relatorios'],
      ['view', 'export'],
    ),
    ...all(['dashboard-executivo', 'dashboard-perspectiva', 'dashboard-area'], ['view', 'export']),
    ...all(['objetivos', 'metas-resultados', 'iniciativas'], ['view', 'approve', 'export']),
    ...all(
      ['planos-acao', 'analise-critica', 'reunioes-decisoes'],
      ['view', 'create', 'edit', 'approve', 'export'],
    ),
  },
  // §9.4: objetivos da área, indicadores sob sua responsabilidade, metas, iniciativas, ações, pendências.
  MANAGER: {
    ...all(['visao-geral', 'dashboard-area', 'previsto-realizado'], ['view', 'export']),
    ...all(['objetivos', 'indicadores'], ['view', 'edit']),
    ...all(['metas-resultados', 'iniciativas'], ['view', 'create', 'edit']),
    'planos-acao': ['view', 'create', 'edit', 'delete'],
    alertas: ['view', 'edit'],
    // Reuniões da área e as de que participa (DEC-049); conduz as análises críticas de Gerência (DEC-050).
    'reunioes-decisoes': ['view', 'create', 'edit'],
    'analise-critica': ['view', 'create', 'edit'],
  },
  // §9.5: lança realizados, atualiza tarefas, anexa evidências, informa desvios.
  OPERATION: {
    'visao-geral': ['view'],
    indicadores: ['view'],
    'metas-resultados': ['view', 'create', 'edit'],
    'planos-acao': ['view', 'edit'],
    alertas: ['view', 'create'],
    // Consulta as reuniões de que participa e as da sua área (DEC-049) e a pauta delas (DEC-050).
    'reunioes-decisoes': ['view'],
    'analise-critica': ['view'],
  },
};

/**
 * Os perfis do PRD §9 recebem as ações acima só onde elas têm efeito (MODULE_ACTIONS — T-281; DEC-088): "aprovar" e
 * "exportar" pedidos para um módulo inteiro ficam só onde alguma rota os confere.
 */
export const DEFAULT_ROLE_PERMISSIONS = Object.fromEntries(
  Object.entries(SEED_ROLE_PERMISSIONS).map(([role, matrix]) => [
    role,
    Object.fromEntries(
      (Object.entries(matrix) as [ModuleKey, ActionKey[]][]).map(([module, actions]) => [
        module,
        actions.filter((a) => actionApplies(module, a)),
      ]),
    ),
  ]),
) as Record<RoleKeyName, PermissionMatrix>;

/** Escopo de área inicial de cada perfil (Gerente e Operação começam restritos às suas áreas). */
export const DEFAULT_AREA_SCOPE: Record<RoleKeyName, AreaScopeName> = {
  FLUXUS_ADMIN: 'ALL',
  FLUXUS_CONSULTANT: 'ALL',
  EXECUTIVE: 'ALL',
  MANAGER: 'OWN',
  OPERATION: 'OWN',
};

/** Módulos que aparecem para o perfil (os que têm "visualizar"). */
export function modulesOf(matrix: PermissionMatrix): ModuleKey[] {
  return MODULE_KEYS.filter((m) => matrix[m]?.includes('view'));
}

export function hasPermission(matrix: PermissionMatrix, module: ModuleKey, action: ActionKey): boolean {
  return matrix[module]?.includes(action) ?? false;
}

/** Módulos de cada perfil com a matriz inicial (atalho para telas e testes). */
export const DEFAULT_ROLE_MODULES: Record<RoleKeyName, ModuleKey[]> = {
  FLUXUS_ADMIN: modulesOf(DEFAULT_ROLE_PERMISSIONS.FLUXUS_ADMIN),
  FLUXUS_CONSULTANT: modulesOf(DEFAULT_ROLE_PERMISSIONS.FLUXUS_CONSULTANT),
  EXECUTIVE: modulesOf(DEFAULT_ROLE_PERMISSIONS.EXECUTIVE),
  MANAGER: modulesOf(DEFAULT_ROLE_PERMISSIONS.MANAGER),
  OPERATION: modulesOf(DEFAULT_ROLE_PERMISSIONS.OPERATION),
};
